/* eslint-disable @typescript-eslint/no-unused-vars */
import { Grid, Paper, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'

import { PartnerNameForm } from '../partnerNameForm/PartnerNameForm'
import { PartnerModuleListTable } from '../partnerModuleListTable/PartnerModuleListTable'
import { PartnerMembers } from '../partnerMembers/PartnerMembers'
import { useBreadcrumbsData } from '../../Layout/breadcrumbsContext/useBreadcrumbsData/useBreadcrumbsData'
import { SSOSettings } from '../ssoSettings/SSOSettings'
import { ProgramsContainer } from '../programs/ProgramsContainer'
import { StaticLinksContainer } from '../staticLinks/StaticLinksContainer'
import { PartnerDeletePanel } from '../partnerDeletePanel/PartnerDeletePanel'
import { PartnerConfig } from '../partnerConfig/PartnerConfig'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { ErrorView, Loader, TabPanel } from '@percent/admin-dashboard/common/components'
import {
  usePermissions,
  useQuery,
  useRequiredParams,
  useStaticLinksAccess
} from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import styles from './PartnerDetail.module.scss'
import { PartnerScopes } from '@percent/admin-dashboard/constants/scopes'
import { Tabs } from '@percent/lemonade'

const getPartnerDetailsRoute = (partnerId: string) => `/partners/${partnerId}`
const getPartnerMembersRoute = (partnerId: string) => `/partners/${partnerId}/members`
const getPartnerSSORoute = (partnerId: string) => `/partners/${partnerId}/sso`
const getPartnerProgramsRoute = (partnerId: string) => `/partners/${partnerId}/programs`
const getPartnerStaticLinksRoute = (partnerId: string) => `/partners/${partnerId}/static-links`
const getPartnerConfigRoute = (partnerId: string) => `/partners/${partnerId}/config`

export function PartnerDetail() {
  const location = useLocation()
  const navigate = useNavigate()
  const { id } = useRequiredParams()
  const { userHasPermissions } = usePermissions()

  const [value, setValue] = useState('')
  const [ssoScope, setSsoScope] = useState<PartnerScopes | undefined>(undefined)
  const { setPath } = useBreadcrumbsData()
  const { adminService } = useServices()
  const [{ data, isLoading, errorMessage }, { refresh: partnerDetailRefresh }] = useQuery(
    adminService.queries.getPartnerDetail,
    { id }
  )

  const [{ data: scopeData, isLoading: scopeIsLoading, errorMessage: scopeErrorMessage }, { refresh: scopeRefresh }] =
    useQuery(adminService.queries.getPartnerScopeList, { partnerId: id })

  useEffect(() => {
    if (location) {
      setValue(`${location.pathname}`)
    }
  }, [location])

  useEffect(() => {
    if (!scopeIsLoading && scopeData) {
      const scopeNames = scopeData.map(({ scopeName }) => scopeName as PartnerScopes)
      setSsoScope(
        scopeNames.find(scope => scope === PartnerScopes.SingleSignOn) ??
          scopeNames.find(scope => scope === PartnerScopes.SingleSignOnNotStrict)
      )
    }
  }, [scopeData, scopeErrorMessage, scopeIsLoading])

  const { t } = useTranslation()
  const programListFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_PROGRAM_LIST)
  const staticLinksFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_STATIC_LINKS)
  const { canRead: canReadStaticLinks } = useStaticLinksAccess()
  const canViewStaticLinks = staticLinksFeatureFlag && canReadStaticLinks
  const internalFgaOrgvValidationSubmissionConfigurationsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_VALIDATION_SUBMISSION_CONFIGURATIONS
  )
  const handleChange = (newValue: string) => {
    setValue(newValue)
    navigate(newValue)
  }

  const name = data?.name

  const hasWorkplaceGivingScope =
    !scopeIsLoading && !!scopeData?.some(({ scopeName }) => scopeName === PartnerScopes.WorkplaceGiving)
  const hasVolunteeringScope =
    !scopeIsLoading && !!scopeData?.some(({ scopeName }) => scopeName === PartnerScopes.Volunteering)

  const isSSOPartner = !!ssoScope
  const canViewSSOSettings = isSSOPartner && userHasPermissions('partner_integrations:can_view')
  const canViewMembers = userHasPermissions('partner_accounts:can_list')

  const permission = (action: string) => userHasPermissions(`partners:${action}`)

  const canEditPartner = userHasPermissions('partners:can_edit')

  const canViewPrograms = internalFgaOrgvValidationSubmissionConfigurationsFeatureFlag
    ? userHasPermissions('validation_submission_configurations:can_read')
    : programListFeatureFlag

  useEffect(() => {
    setPath(name)

    return () => setPath(undefined)
  }, [name, setPath])

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Grid container spacing={0} component={Paper} elevation={0} className={styles.partnerDetailContainer}>
      <Grid item xs={12} className={styles.partnerDetail}>
        <Grid item xs={12}>
          <Typography className={styles.partnerName} variant="h5">
            {name}
          </Typography>
        </Grid>
        <Grid item xs={12} className={styles.partnerButtons}>
          <Tabs
            activeTab={value}
            onChangeTab={handleChange}
            items={[
              { key: getPartnerDetailsRoute(id), label: t('typography.partnerDetails') },
              ...(canViewMembers ? [{ key: getPartnerMembersRoute(id), label: t('typography.members') }] : []),
              ...(canViewSSOSettings ? [{ key: getPartnerSSORoute(id), label: t('typography.singleSignOn') }] : []),
              ...(canViewPrograms ? [{ key: getPartnerProgramsRoute(id), label: t('typography.programs') }] : []),
              ...(canViewStaticLinks
                ? [{ key: getPartnerStaticLinksRoute(id), label: t('typography.staticLinks') }]
                : []),
              ...(hasWorkplaceGivingScope
                ? [{ key: getPartnerConfigRoute(id), label: t('typography.partnerConfig') }]
                : [])
            ]}
          />
          <div className={styles.indicator} />
        </Grid>
        <TabPanel value={value} index={getPartnerDetailsRoute(id)}>
          <Grid>
            <PartnerNameForm
              partnerId={id}
              partnerName={name}
              refreshApi={partnerDetailRefresh}
              editEnabled={canEditPartner}
            />
          </Grid>
          {permission('can_list_scopes') && (
            <Grid>
              <PartnerModuleListTable
                partnerId={id}
                data={scopeData}
                isLoading={scopeIsLoading}
                errorMessage={scopeErrorMessage}
                refreshApi={scopeRefresh}
              />
            </Grid>
          )}
          {permission('can_delete') && (
            <Grid>
              <PartnerDeletePanel partnerId={id} partnerName={name} />
            </Grid>
          )}
        </TabPanel>

        {canViewMembers && (
          <TabPanel value={value} index={getPartnerMembersRoute(id)}>
            <Grid className={styles.memberTableGrid}>
              <PartnerMembers ssoScope={ssoScope} />
            </Grid>
          </TabPanel>
        )}
        {canViewSSOSettings && (
          <TabPanel value={value} index={getPartnerSSORoute(id)}>
            <Grid className={styles.sso}>
              <SSOSettings partnerId={id} />
            </Grid>
          </TabPanel>
        )}
        {canViewPrograms && (
          <TabPanel value={value} index={getPartnerProgramsRoute(id)}>
            <Grid className={styles.memberTableGrid}>
              <ProgramsContainer partner={data} />
            </Grid>
          </TabPanel>
        )}
        {canViewStaticLinks && (
          <TabPanel value={value} index={getPartnerStaticLinksRoute(id)}>
            <Grid className={styles.memberTableGrid}>
              <StaticLinksContainer partnerId={id} />
            </Grid>
          </TabPanel>
        )}
        {hasWorkplaceGivingScope && (
          <TabPanel value={value} index={getPartnerConfigRoute(id)}>
            <Grid className={styles.memberTableGrid}>
              <PartnerConfig
                partnerId={id}
                hasWorkplaceGivingScope={hasWorkplaceGivingScope}
                hasVolunteeringScope={hasVolunteeringScope}
              />
            </Grid>
          </TabPanel>
        )}
      </Grid>
    </Grid>
  )
}
